/**
 * Pinia approval-workspace store (Phase 2.1 -- card
 * https://trello.com/c/hYlwyKYT).
 *
 * Backs the distributor Approval Workspace at
 * ``/distributor/decorations/approvals`` (list view) and
 * ``/distributor/decorations/approvals/:approvalId`` (detail view).
 *
 * Data flow
 * ---------
 *
 * 1. ``fetchQueue()`` reads GET /api/approval_records -- the flat list
 *    per P2-Q1. The store groups client-side via ``filters.groupByClient``
 *    so the queue can be reshuffled by filter without a server roundtrip.
 * 2. ``selectApproval(id)`` reads GET /api/approval_records/<id> (which
 *    inlines thread_messages + actions per F1.e).
 * 3. ``fetchUsage(artworkId)`` reads GET /api/artworks/<id>/usage -- the
 *    Phase 2/Phase 3 shared endpoint backing WhereUsedPanel.
 * 4. ``fetchClients()`` reads GET /api/clients/ for the per-client
 *    grouping + display.
 * 5. ``fetchBypassReasons()`` reads GET /api/bypass_reasons/ for the
 *    shared ReasonPicker.
 *
 * Mutations (all POST through the F1.e endpoint surface):
 *   - sendReminder, resendApprovalRequest, escalate, bypass, resend,
 *     postMessage, restoreVersion.
 * Each action is optimistic: on success the local queue + detail are
 * patched without a refetch; on error the queue is refetched to
 * re-establish ground truth and the error surfaces to the caller.
 *
 * Tenant + epoch hygiene
 * ----------------------
 *
 * Mirrors useDistributorSummaryStore / useArtworksStore: an integer
 * ``epoch`` is bumped by ``clear()`` so a late-arriving fetch from a
 * prior tenant cannot repopulate state after impersonation stops.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import {
    computeCounts,
    groupByClient,
    matchesFilter,
} from '@/pages/distributor/approvals/filters'

/** Plain-language note for the API's ``dtf_not_sent {count}`` hint, else null. */
function dtfNotSentNotice(hint) {
    const n = Number(hint?.count)
    if (!Number.isFinite(n) || n < 1) return null
    const subject =
        n === 1 ? '1 DTF placement was' : `${n} DTF placements were`
    return `${subject} not sent. DTF art is not sent to the digitizer unless you choose art cleanup.`
}

export const useApprovalWorkspaceStore = defineStore(
    'approvalWorkspace',
    () => {
        // ---- state ----
        const queue = ref([]) // flat ApprovalRecord list (server-sorted).
        const queueLoading = ref(false)
        const queueError = ref(null)

        const selectedId = ref(null)
        const selectedDetail = ref(null) // record + thread_messages + actions
        const detailLoading = ref(false)
        const detailError = ref(null)

        const usageByArtwork = ref({}) // { [artworkId]: {campaign_products, stores} }
        const usageLoadingByArtwork = ref({}) // { [artworkId]: bool }

        const clients = ref([]) // [{id, name, ...}]
        const clientsLoading = ref(false)

        const bypassReasons = ref([])
        const bypassReasonsLoading = ref(false)

        const filter = ref('needs_attention')

        const actionPending = ref(false)
        const actionError = ref(null)
        // A non-error outcome worth telling the user (e.g. a Resend the
        // approval round is already covering).
        const actionNotice = ref(null)

        // ---- digitizer rework (card https://trello.com/c/FKGMWf9Z) ----
        //
        // ``reworkPrefill`` is the CLIENT'S latest change request, verbatim,
        // as the API resolved it -- the starting point for the distributor's
        // edit, never the thing we submit. ``reworkCanSubmit`` is whether a
        // reworkable digitizer job exists behind the record; it is
        // deliberately NOT "did the client say something", because a
        // distributor may trigger a rework on their own.
        const reworkPrefill = ref(null)
        const reworkCanSubmit = ref(false)

        let epoch = 0

        // ---- getters ----

        /**
         * Client lookup by id, used by ``groupByClient`` so each group
         * carries the full client record (name, contact, etc.).
         */
        const clientsById = computed(() => {
            const out = {}
            for (const c of clients.value) {
                if (c && c.id != null) out[c.id] = c
            }
            return out
        })

        /**
         * Current filter applied to the flat queue. Pure computed -- no
         * server roundtrip.
         */
        const filteredQueue = computed(() =>
            queue.value.filter((e) => matchesFilter(e, filter.value)),
        )

        /**
         * Grouped-by-client structure that the queue list renders.
         */
        const groupedByClient = computed(() =>
            groupByClient(filteredQueue.value, clientsById.value),
        )

        /**
         * Counts for the summary header + filter chip badges.
         */
        const counts = computed(() => computeCounts(queue.value))

        /**
         * The currently-open ApprovalRecord (detail panel input).
         */
        const selectedRecord = computed(() => selectedDetail.value)

        // ---- helpers ----

        function _patchRecord(updated) {
            if (!updated || updated.id == null) return
            const idx = queue.value.findIndex((e) => e.id === updated.id)
            if (idx >= 0) {
                // Preserve enrichment fields (e.g. artwork.pipeline_state)
                // that the queue feed may carry but the detail-shaped
                // record does not.
                queue.value[idx] = { ...queue.value[idx], ...updated }
            }
            if (
                selectedDetail.value &&
                selectedDetail.value.id === updated.id
            ) {
                selectedDetail.value = {
                    ...selectedDetail.value,
                    ...updated,
                }
            }
        }

        // ---- actions ----

        async function fetchQueue() {
            const myEpoch = epoch
            queueLoading.value = true
            queueError.value = null
            try {
                const res = await api.get('/approval_records')
                if (myEpoch !== epoch) return
                const list = Array.isArray(res?.data?.approval_records)
                    ? res.data.approval_records
                    : []
                queue.value = list
            } catch (e) {
                if (myEpoch !== epoch) return
                queueError.value =
                    e?.response?.data?.error || e?.message || 'fetch failed'
                queue.value = []
            } finally {
                if (myEpoch === epoch) queueLoading.value = false
            }
        }

        async function fetchClients() {
            const myEpoch = epoch
            clientsLoading.value = true
            try {
                const res = await api.get('/clients/')
                if (myEpoch !== epoch) return
                // SAFRS list returns JSON:API ``{data: [...]}``; the
                // workspace only needs a flat array of {id, name, ...}.
                const data = res?.data?.data
                if (Array.isArray(data)) {
                    clients.value = data.map((row) => ({
                        id: row.id,
                        ...(row.attributes || {}),
                    }))
                } else if (Array.isArray(res?.data?.clients)) {
                    clients.value = res.data.clients
                } else {
                    clients.value = []
                }
            } catch (e) {
                if (myEpoch !== epoch) return
                clients.value = []
            } finally {
                if (myEpoch === epoch) clientsLoading.value = false
            }
        }

        async function fetchBypassReasons() {
            if (bypassReasons.value.length > 0) return
            const myEpoch = epoch
            bypassReasonsLoading.value = true
            try {
                const res = await api.get('/bypass_reasons/')
                if (myEpoch !== epoch) return
                const data = res?.data?.data
                if (Array.isArray(data)) {
                    bypassReasons.value = data
                        .map((row) => ({
                            id: row.id,
                            ...(row.attributes || {}),
                        }))
                        .filter(
                            (r) =>
                                r.is_active === undefined || r.is_active === true,
                        )
                } else {
                    bypassReasons.value = []
                }
            } catch (e) {
                if (myEpoch !== epoch) return
                bypassReasons.value = []
            } finally {
                if (myEpoch === epoch) bypassReasonsLoading.value = false
            }
        }

        async function selectApproval(id) {
            if (id == null) {
                selectedId.value = null
                selectedDetail.value = null
                detailError.value = null
                return
            }
            selectedId.value = id
            const myEpoch = epoch
            detailLoading.value = true
            detailError.value = null
            try {
                const res = await api.get(`/approval_records/${id}`)
                if (myEpoch !== epoch) return
                selectedDetail.value = res?.data || null
            } catch (e) {
                if (myEpoch !== epoch) return
                detailError.value =
                    e?.response?.data?.error ||
                    e?.message ||
                    'Could not load this approval.'
                selectedDetail.value = null
            } finally {
                if (myEpoch === epoch) detailLoading.value = false
            }
        }

        async function fetchUsage(artworkId) {
            if (artworkId == null) return null
            // Cached: same Artwork id within the session.
            if (usageByArtwork.value[artworkId]) {
                return usageByArtwork.value[artworkId]
            }
            const myEpoch = epoch
            usageLoadingByArtwork.value = {
                ...usageLoadingByArtwork.value,
                [artworkId]: true,
            }
            try {
                const res = await api.get(`/artworks/${artworkId}/usage`)
                if (myEpoch !== epoch) return null
                const payload = res?.data || {
                    campaign_products: [],
                    stores: [],
                }
                usageByArtwork.value = {
                    ...usageByArtwork.value,
                    [artworkId]: payload,
                }
                return payload
            } catch (e) {
                if (myEpoch !== epoch) return null
                return { campaign_products: [], stores: [] }
            } finally {
                usageLoadingByArtwork.value = {
                    ...usageLoadingByArtwork.value,
                    [artworkId]: false,
                }
            }
        }

        function setFilter(key) {
            filter.value = key
        }

        // ---- mutations (POSTs through the F1.e endpoint surface) ----

        async function _runAction(fn) {
            actionPending.value = true
            actionError.value = null
            actionNotice.value = null
            try {
                const result = await fn()
                return result
            } catch (e) {
                actionError.value =
                    e?.response?.data?.error ||
                    e?.message ||
                    'Action failed.'
                throw e
            } finally {
                actionPending.value = false
            }
        }

        async function sendReminder(recordId) {
            return _runAction(async () => {
                await api.post(`/approval_records/${recordId}/remind`)
                // Reload detail to pick up the new thread message.
                if (selectedId.value === recordId) {
                    await selectApproval(recordId)
                }
            })
        }

        async function escalate(recordId) {
            return _runAction(async () => {
                await api.post(`/approval_records/${recordId}/escalate`)
                if (selectedId.value === recordId) {
                    await selectApproval(recordId)
                }
            })
        }

        async function resendApprovalRequest(recordId) {
            return _runAction(async () => {
                const res = await api.post(`/approval_records/${recordId}/resend`)
                actionNotice.value = res?.data?.notice || null
                if (selectedId.value === recordId) {
                    await selectApproval(recordId)
                }
            })
        }

        async function bypass(recordId, reasonId, notes) {
            return _runAction(async () => {
                const res = await api.post(
                    `/approval_records/${recordId}/bypass`,
                    { reason_id: reasonId, notes: notes || null },
                )
                _patchRecord(res?.data)
                if (selectedId.value === recordId) {
                    await selectApproval(recordId)
                }
                return res?.data
            })
        }

        async function postMessage(recordId, body) {
            return _runAction(async () => {
                await api.post(
                    `/approval_records/${recordId}/messages`,
                    { body },
                )
                if (selectedId.value === recordId) {
                    await selectApproval(recordId)
                }
            })
        }

        /**
         * Mint a new ArtworkVersion that re-uses an older version's
         * ``file_url`` (P3-Q7: restore mints a NEW version; no
         * in-place edit). The Builder's lifecycle move from
         * changes_requested -> digitizing is the caller's next step;
         * this action only mints the version row.
         */
        async function restoreVersion(artworkId, sourceVersion) {
            return _runAction(async () => {
                if (!sourceVersion || !sourceVersion.file_url) {
                    throw new Error(
                        'Cannot restore: the source version has no file URL.',
                    )
                }
                const body = { file_url: sourceVersion.file_url }
                if (sourceVersion.digitizer_id != null) {
                    body.digitizer_id = sourceVersion.digitizer_id
                }
                if (sourceVersion.digitized_file_url) {
                    body.digitized_file_url =
                        sourceVersion.digitized_file_url
                }
                const res = await api.post(
                    `/artworks/${artworkId}/versions`,
                    body,
                )
                // Invalidate any cached usage for this artwork; version
                // history changed but where-used has not, so we only
                // need to bust usage if a future field starts mirroring
                // version. For now: no-op.
                return res?.data
            })
        }

        /**
         * Re-route an artwork to its digitizer. Used by the
         * StatusAwareActionBar for the ``uploaded`` state ("Send to
         * digitizer") and for the ``changes_requested`` resend-with-
         * corrections loop.
         */
        async function sendToDigitizer(artworkId) {
            return _runAction(async () => {
                const res = await api.post(
                    `/artworks/${artworkId}/send_to_digitizer`,
                )
                // The artwork's pipeline_state changed; refetch the
                // queue so the row migrates between filter chips.
                await fetchQueue()
                if (selectedId.value != null) {
                    await selectApproval(selectedId.value)
                }
                // Not an error: the send went out, but DTF placements in
                // scope were left out (no acknowledged cleanup attached).
                actionNotice.value = dtfNotSentNotice(res?.data?.dtf_not_sent)
            })
        }

        /**
         * Load the rework prefill for a record.
         *
         * Read-only and safe to call on selection -- it reaches no vendor.
         * Failures are swallowed into "nothing to prefill, cannot submit"
         * rather than raised: the rework panel is one section of a detail
         * view, and a missing prefill must not blank the page around it.
         * A genuine submit failure is surfaced loudly by submitRework.
         */
        async function fetchRework(recordId) {
            try {
                const res = await api.get(
                    `/approval_records/${recordId}/rework`,
                )
                reworkPrefill.value = res?.data?.instruction ?? null
                reworkCanSubmit.value = Boolean(res?.data?.can_submit)
                return res?.data
            } catch (e) {
                reworkPrefill.value = null
                reworkCanSubmit.value = false
                return null
            }
        }

        /**
         * Submit the distributor's EDITED instruction to the digitizer.
         *
         * ``instruction`` is what the distributor actually typed. It is
         * deliberately passed through rather than re-derived from
         * ``reworkPrefill`` -- substituting the client's original here would
         * make the whole edit surface decorative.
         *
         * Upstream this becomes ``POST /api/order/edit/{orderID}``, which is
         * BILLABLE and NON-idempotent. The API refuses a second concurrent
         * submit with 409 and the panel latches its button; neither is
         * relaxed here.
         *
         * ``reworkPrefill`` is NOT cleared on failure -- see the test. On
         * success the detail is reloaded so the new system thread message
         * ("Sent to digitizer for rework") appears.
         */
        async function submitRework(recordId, instruction) {
            return _runAction(async () => {
                const res = await api.post(
                    `/approval_records/${recordId}/rework`,
                    { instruction },
                )
                if (selectedId.value === recordId) {
                    await selectApproval(recordId)
                    await fetchRework(recordId)
                }
                return res?.data
            })
        }

        function clear() {
            epoch += 1
            reworkPrefill.value = null
            reworkCanSubmit.value = false
            queue.value = []
            queueLoading.value = false
            queueError.value = null
            selectedId.value = null
            selectedDetail.value = null
            detailLoading.value = false
            detailError.value = null
            usageByArtwork.value = {}
            usageLoadingByArtwork.value = {}
            clients.value = []
            clientsLoading.value = false
            bypassReasons.value = []
            bypassReasonsLoading.value = false
            filter.value = 'needs_attention'
            actionPending.value = false
            actionError.value = null
            actionNotice.value = null
        }

        return {
            // state
            queue,
            queueLoading,
            queueError,
            selectedId,
            selectedDetail,
            detailLoading,
            detailError,
            usageByArtwork,
            usageLoadingByArtwork,
            clients,
            clientsLoading,
            bypassReasons,
            bypassReasonsLoading,
            filter,
            actionPending,
            actionError,
            actionNotice,
            reworkPrefill,
            reworkCanSubmit,
            // getters
            clientsById,
            filteredQueue,
            groupedByClient,
            counts,
            selectedRecord,
            // actions
            fetchQueue,
            fetchClients,
            fetchBypassReasons,
            selectApproval,
            fetchUsage,
            setFilter,
            sendReminder,
            escalate,
            resendApprovalRequest,
            bypass,
            postMessage,
            restoreVersion,
            sendToDigitizer,
            fetchRework,
            submitRework,
            clear,
        }
    },
)
